import type { Task } from '../types';

const TASKS_CACHE_KEY = 'tasks_cache';

// Helper function to update localStorage cache
function updateTasksCache(tasks: Task[]) {
  localStorage.setItem(TASKS_CACHE_KEY, JSON.stringify(tasks));
}

// Helper function to get tasks from cache
function getTasksFromCache(): Task[] {
  const cached = localStorage.getItem(TASKS_CACHE_KEY);
  if (!cached) return [];
  try {
    return JSON.parse(cached);
  } catch {
    return [];
  }
}

export async function getTasks(userId: string): Promise<Task[]> {
  // Supabase disabled: only use cache
  return getTasksFromCache();
}

export async function addTask(userId: string, task: Omit<Task, 'id'>): Promise<Task | null> {
  const tasks = getTasksFromCache();
  const newTask: Task = {
    ...task,
    id: crypto.randomUUID(),
  };
  const updatedTasks = [...tasks, newTask];
  updateTasksCache(updatedTasks);
  // Supabase disabled: do not sync
  return newTask;
}

export async function updateTask(userId: string, taskId: string, updates: Partial<Task>): Promise<Task | null> {
  const tasks = getTasksFromCache();
  const updatedTasks = tasks.map(task => 
    task.id === taskId ? { ...task, ...updates } : task
  );
  updateTasksCache(updatedTasks);
  // Supabase disabled: do not sync
  return updatedTasks.find(t => t.id === taskId) || null;
}

export async function deleteTask(userId: string, taskId: string): Promise<boolean> {
  const tasks = getTasksFromCache();
  const updatedTasks = tasks.filter(task => task.id !== taskId);
  updateTasksCache(updatedTasks);
  // Supabase disabled: do not sync
  return true;
}

export async function markTaskAsCompleted(userId: string, taskId: string): Promise<boolean> {
  return updateTask(userId, taskId, { status: 'Completed' }) !== null;
}

export async function markTaskAsIncomplete(userId: string, taskId: string): Promise<boolean> {
  return updateTask(userId, taskId, { status: 'Not Started' }) !== null;
} 